import { useEffect, useLayoutEffect, useRef, type KeyboardEvent, type RefObject } from 'react'
import { foraDoConjunto } from './painel-flutuante.tsx'

const CONTROLES = 'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]'

function controles(painel: HTMLElement): HTMLElement[] {
  return Array.from(painel.querySelectorAll<HTMLElement>(CONTROLES)).filter((item) => item.getClientRects().length > 0)
}

/** Mantém o percurso de foco do campo quando o painel passa a viver num portal. */
export function useMenuFlutuante({
  aberto, pronto, aoFechar, aoAbrir, caixa, gatilho, idDoPainel, focarAoAbrir = true, navegarComSetas = true,
}: {
  aberto: boolean
  pronto: boolean
  aoFechar: () => void
  aoAbrir?: () => void
  caixa: RefObject<HTMLElement | null>
  gatilho: RefObject<HTMLElement | null>
  idDoPainel: string
  focarAoAbrir?: boolean
  navegarComSetas?: boolean
}) {
  const atual = useRef({ aoFechar, caixa, gatilho, idDoPainel, focarAoAbrir })
  atual.current = { aoFechar, caixa, gatilho, idDoPainel, focarAoAbrir }

  function fechar(): void {
    atual.current.aoFechar()
    atual.current.gatilho.current?.focus()
  }

  function focar(primeiro = true): boolean {
    const painel = document.getElementById(idDoPainel)
    if (painel === null) return false
    const itens = controles(painel)
    const ativo = painel.querySelector<HTMLElement>('[aria-checked="true"], [aria-selected="true"], [aria-pressed="true"]')
    const destino = primeiro ? ativo ?? itens[0] : itens.at(-1)
    destino?.focus()
    return destino !== undefined
  }

  // O portal só existe depois da primeira medida. Focar antes da pintura evita que uma
  // tarefa de animação atrasada substitua o foco que a pessoa já moveu com o teclado.
  useLayoutEffect(() => {
    if (!aberto || !pronto || !atual.current.focarAoAbrir) return
    const painel = document.getElementById(atual.current.idDoPainel)
    if (painel === null) return
    const ativo = painel.querySelector<HTMLElement>('[aria-checked="true"], [aria-selected="true"], [aria-pressed="true"]')
    ;(ativo ?? controles(painel)[0])?.focus()
  }, [aberto, pronto])

  useEffect(() => {
    if (!aberto) return undefined
    const fora = (evento: MouseEvent): void => {
      if (foraDoConjunto(evento.target, atual.current.caixa.current)) atual.current.aoFechar()
    }
    const escape = (evento: globalThis.KeyboardEvent): void => {
      if (evento.key === 'Tab' && evento.target === atual.current.gatilho.current) {
        if (evento.shiftKey) { atual.current.aoFechar(); return }
        const painel = document.getElementById(atual.current.idDoPainel)
        const primeiro = painel === null ? undefined : controles(painel)[0]
        if (primeiro !== undefined) { evento.preventDefault(); primeiro.focus() }
        else atual.current.aoFechar()
        return
      }
      if (evento.key !== 'Escape') return
      // Uma lista filha (Ação/Carteira, por exemplo) consome o seu próprio Escape primeiro.
      const topo = Array.from(document.querySelectorAll<HTMLElement>('[data-painel-flutuante]')).at(-1)
      if (topo?.id !== atual.current.idDoPainel) return
      evento.preventDefault()
      evento.stopPropagation()
      atual.current.aoFechar()
      atual.current.gatilho.current?.focus()
    }
    document.addEventListener('mousedown', fora)
    document.addEventListener('keydown', escape, true)
    return () => {
      document.removeEventListener('mousedown', fora)
      document.removeEventListener('keydown', escape, true)
    }
  }, [aberto])

  function aoTeclarNoGatilho(evento: KeyboardEvent<HTMLElement>): void {
    if (evento.shiftKey) return
    if (!aberto) {
      if (aoAbrir !== undefined && (evento.key === 'ArrowDown' || evento.key === 'ArrowUp')) { evento.preventDefault(); aoAbrir() }
      return
    }
    if (evento.key === 'Tab' || evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
      if (focar(evento.key !== 'ArrowUp')) evento.preventDefault()
      else if (evento.key === 'Tab') atual.current.aoFechar()
    }
  }

  function aoTeclarNoPainel(evento: KeyboardEvent<HTMLElement>): void {
    const painel = document.getElementById(idDoPainel)
    if (painel === null) return
    const itens = controles(painel)
    const indice = itens.indexOf(document.activeElement as HTMLElement)
    if (navegarComSetas && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(evento.key)) {
      evento.preventDefault()
      const destino = evento.key === 'Home' ? 0 : evento.key === 'End' ? itens.length - 1
        : (indice + (evento.key === 'ArrowDown' ? 1 : -1) + itens.length) % itens.length
      itens[destino]?.focus()
    }
    if (evento.key !== 'Tab') return
    if (evento.shiftKey && indice === 0) {
      evento.preventDefault()
      fechar()
    } else if (!evento.shiftKey && indice === itens.length - 1) {
      evento.preventDefault()
      const campo = gatilho.current
      const foraDoPainel = Array.from(document.querySelectorAll<HTMLElement>(CONTROLES))
        .filter((item) => item.getClientRects().length > 0 && item.closest('[inert], [data-painel-flutuante]') === null)
      const seguinte = campo === null ? undefined : foraDoPainel[foraDoPainel.indexOf(campo) + 1]
      atual.current.aoFechar()
      ;(seguinte ?? campo)?.focus()
    }
  }

  return { fechar, aoTeclarNoGatilho, aoTeclarNoPainel }
}
